// Attribution tracking — UTM capture, Yandex.Metrika ClientID, reachGoal helpers.
// All functions are wrapped in try/catch so tracking failures never break the product.
// Активные боты см. ENABLED_BOTS — управляется централизованно вместе с
// counter-картой в back/lib/metrika-helpers.php::getMetrikaCounterIdForBot().

const LS_FIRST = 'attribution_first_touch';
const LS_LAST = 'attribution_last_touch';
const LS_CID = 'ym_client_id';
// Set ONLY inside the getClientID callback (= tag.js loaded, our ym('hit')
// drained, counter saw this CID). Cached LS_CID returned by the timeout
// fallback does NOT flip this — that path runs under adblock/network failure
// where the hit never reached mc.yandex.ru, so the CID is orphan for our
// counter and would produce LINKAGE_FAILURE. The backend gate keys on this
// flag to avoid feeding orphan CIDs into the offline-conversions queue.
const LS_CID_CONFIRMED = 'ym_cid_confirmed';

const ENABLED_BOTS = ['clipcut', 'meetscribe', 'vid2text'];

// Module-level Promise that resolves with the Metrika ClientID once available.
// storeClientID() initializes this lazily; whenClientIdReady() awaits it with
// a timeout so login/saveUser never block on Metrika being slow to respond.
let clientIdPromise = null;

export function isTrackingEnabled(chatbotName) {
  return ENABLED_BOTS.includes(chatbotName);
}

export function captureUTMs() {
  try {
    const p = new URLSearchParams(window.location.search);
    const hasUtm = p.get('utm_source') || p.get('utm_campaign') || p.get('utm_medium');
    if (!hasUtm) return;

    const utm = {
      utm_source: p.get('utm_source') || '',
      utm_campaign: p.get('utm_campaign') || '',
      utm_medium: p.get('utm_medium') || '',
      utm_content: p.get('utm_content') || '',
      utm_term: p.get('utm_term') || '',
      landing_url: window.location.href,
      landed_at: new Date().toISOString(),
    };

    if (!localStorage.getItem(LS_FIRST)) {
      localStorage.setItem(LS_FIRST, JSON.stringify(utm));
    }
    localStorage.setItem(LS_LAST, JSON.stringify(utm));
  } catch (e) {
    // tracking must never throw
  }
}

// Kick off the Metrika getClientID callback. Safe to call multiple times —
// only the first call actually queries; subsequent calls reuse the same Promise.
// The 5s safety timer handles the case where the Metrika tag never loads
// (adblock, network failure) so the Promise never hangs forever.
export function storeClientID(metrikaId) {
  if (clientIdPromise) return clientIdPromise;
  clientIdPromise = new Promise((resolve) => {
    let settled = false;
    const finish = (cid) => {
      if (settled) return;
      settled = true;
      try { if (cid) localStorage.setItem(LS_CID, cid); } catch (e) { /* noop */ }
      resolve(cid || null);
    };
    try {
      if (!metrikaId) { finish(null); return; }
      // vue3-yandex-metrika injects tag.js asynchronously (script.onload) and
      // does NOT install the canonical command-queue stub up front. So when we
      // call ym() immediately, window.ym may not exist yet. Install the stub
      // ourselves — tag.js drains queued calls once it loads.
      if (!window.ym) {
        window.ym = function () { (window.ym.a = window.ym.a || []).push(arguments); };
        window.ym.l = +new Date();
      }
      // Register a hit for this counter BEFORE asking for the CID. Both calls
      // queue in window.ym.a; tag.js drains them in order. This guarantees the
      // counter has seen at least one hit from this CID before we ship it as
      // an offline conversion — otherwise Metrika returns LINKAGE_FAILURE.
      window.ym(metrikaId, 'hit', window.location.href, {
        title: document.title || '',
        referer: document.referrer || '',
      });
      window.ym(metrikaId, 'getClientID', function (cid) {
        // Reaching this callback means tag.js loaded and drained ym.a — both
        // our ym('hit') and ym('getClientID') ran for real. Mark the CID as
        // confirmed so the backend gate accepts it for offline conversions.
        try { if (cid) localStorage.setItem(LS_CID_CONFIRMED, '1'); } catch (e) { /* noop */ }
        finish(cid);
      });
      // Failsafe: if the tag never loads (adblock etc.) resolve with whatever
      // is in storage (typically null) so awaiters move on. We deliberately do
      // NOT set LS_CID_CONFIRMED here — the cached CID may have been issued
      // by another counter/visit and we have no proof Metrika saw a hit on
      // OUR counter for it.
      setTimeout(() => finish(localStorage.getItem(LS_CID) || null), 5000);
    } catch (e) {
      finish(null);
    }
  });
  return clientIdPromise;
}

export function getStoredClientID() {
  try { return localStorage.getItem(LS_CID) || null; } catch (e) { return null; }
}

// Resolve with the CID if it arrives within timeoutMs, else with whatever is
// currently in localStorage (typically null on first visit). Used by callers
// that want to attach the CID to a request but cannot block forever.
export function whenClientIdReady(timeoutMs = 2000) {
  const cached = getStoredClientID();
  if (cached) return Promise.resolve(cached);
  if (!clientIdPromise) return Promise.resolve(null);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (val) => { if (!settled) { settled = true; resolve(val); } };
    clientIdPromise.then((cid) => finish(cid));
    setTimeout(() => finish(getStoredClientID()), timeoutMs);
  });
}

export function getAttributionPayload() {
  try {
    const first = localStorage.getItem(LS_FIRST);
    const last = localStorage.getItem(LS_LAST);
    return {
      first_touch: first ? JSON.parse(first) : null,
      last_touch: last ? JSON.parse(last) : null,
      ym_client_id: getStoredClientID(),
      ym_client_id_confirmed: localStorage.getItem(LS_CID_CONFIRMED) === '1',
    };
  } catch (e) {
    return { first_touch: null, last_touch: null, ym_client_id: null, ym_client_id_confirmed: false };
  }
}

export function trackGoal(metrikaId, goalName, params) {
  try {
    if (!window.ym || !metrikaId || !goalName) return;
    if (params) {
      window.ym(metrikaId, 'reachGoal', goalName, params);
    } else {
      window.ym(metrikaId, 'reachGoal', goalName);
    }
  } catch (e) { /* noop */ }
}
